import { ROUTES } from "@nf/constants";
import Script from "next/script";
import { useEffect } from "react";

import { useLanguage, useRouter } from "../../hooks";

declare global {
  interface Window {
    _dixa_?: {
      invoke: (
        method: string,
        payload?: { messengerToken: string; language?: string }
      ) => void;
      addListener: (event: string, listener: () => void) => void;

      api?: {
        init: (payload: {
          messengerToken: string;
          language?: string;
        }) => Promise<void>;
        shutdown: () => Promise<void>;
      };
    };
  }
}

const DIXA_DISABLED_ROUTES = [
  ROUTES.PLAYER_SCREEN,
  ROUTES.PLAYER_CHANNEL_SCREEN,
];

const closeDixaWidget = () => {
  return window._dixa_?.api?.shutdown();
};

const restartDixaWidget = async (messengerToken: string, language: string) => {
  await closeDixaWidget();
  await window._dixa_?.api?.init({ messengerToken, language });
};

interface DixaScriptProps {
  messengerToken: string;
}

export const DixaScript = ({ messengerToken }: DixaScriptProps) => {
  const language = useLanguage();
  const router = useRouter();

  useEffect(() => {
    const shouldBeDisabled = DIXA_DISABLED_ROUTES.some((route) =>
      router.pathname.includes(route)
    );

    if (shouldBeDisabled) {
      closeDixaWidget();

      return () => {
        restartDixaWidget(messengerToken, language);
      };
    }
  }, [router.pathname]);

  return (
    <Script
      id="dixa"
      strategy="lazyOnload"
      dangerouslySetInnerHTML={{
        __html: `
          (function (w, d, s) {
            if (w._dixa_) return;
            w._dixa_ = {
              invoke: function (m, a) {
                w._dixa_.pendingCalls = w._dixa_.pendingCalls || [];
                w._dixa_.pendingCalls.push([m, a]);
              },
              addListener: function (event, listener) {
                w._dixa_.pendingAddListenerCalls = w._dixa_.pendingAddListenerCalls || [];
                w._dixa_.pendingAddListenerCalls.push([event, listener]);
              },
            };
            s = d.createElement("script");
            s.type = "text/javascript";
            s.setAttribute("charset", "utf-8");
            s.async = true;
            s.src = "https://messenger.dixa.io/bootstrap.js";
            var before = d.getElementsByTagName("script")[0];
            before.parentNode.insertBefore(s, before);
          })(window, document);
          _dixa_.invoke("init", {
            messengerToken: "${messengerToken}", 
            language: "${language}",
          });
        `,
      }}
    />
  );
};
